import type { SelectOption } from '@invara-dev/react-base';
import {
  ApprovalRuleOverrideType,
  type RateCapRuleOverrideModel,
  type SelfBookingRequiresApprovalOverrideModel
} from 'src/features/company/approvals/types/approvalConfigTypes';

export type RuleOverrideScope = RateCapRuleOverrideModel | SelfBookingRequiresApprovalOverrideModel;

export function doesContactTagOverrideApply(
  overrideTags: string[] | undefined,
  travellerTags: string[] | undefined
): boolean {
  if (!overrideTags?.length || !travellerTags?.length) {
    return false;
  }

  const normalizedTravellerTags = travellerTags.map((tag) => tag.toLowerCase());
  return overrideTags.some((tag) => normalizedTravellerTags.includes(tag.toLowerCase()));
}

export function getMatchingContactTagValues(
  overrideTags: string[] | undefined,
  travellerTags: string[] | undefined
): string[] {
  if (!overrideTags?.length || !travellerTags?.length) {
    return [];
  }

  const normalizedTravellerTags = new Map(travellerTags.map((tag) => [tag.toLowerCase(), tag]));
  return overrideTags.filter((tag) => normalizedTravellerTags.has(tag.toLowerCase()));
}

export function formatOverrideType(type: ApprovalRuleOverrideType): string {
  switch (type) {
    case ApprovalRuleOverrideType.Cities:
      return 'Cities';
    case ApprovalRuleOverrideType.CityTags:
      return 'City Tags';
    case ApprovalRuleOverrideType.Contacts:
      return 'Contacts';
    case ApprovalRuleOverrideType.ContactTags:
      return 'Contact Tags';
    case ApprovalRuleOverrideType.None:
      return 'None';
    default:
      return String(type);
  }
}

export function formatOverrideScope(override: RuleOverrideScope): string {
  const parts: string[] = [];
  if (override.CityIds?.length) parts.push(`Cities: ${override.CityIds.length}`);
  if (override.CityTags?.length) parts.push(`City Tags: ${override.CityTags.length}`);
  if (override.ContactIds?.length) parts.push(`Contacts: ${override.ContactIds.length}`);
  if (override.ContactTags?.length) parts.push(`Contact Tags: ${override.ContactTags.length}`);
  return parts.length ? parts.join('; ') : '—';
}

export function getOverrideDetail(override: RuleOverrideScope): {
  values: string[];
  emptyText: string;
} {
  switch (override.OverrideType) {
    case ApprovalRuleOverrideType.Cities:
      return {
        values: formatCityValues(override),
        emptyText: 'No cities selected.'
      };
    case ApprovalRuleOverrideType.CityTags:
      return {
        values: override.CityTags ?? [],
        emptyText: 'No city tags selected.'
      };
    case ApprovalRuleOverrideType.Contacts:
      return {
        values: formatContactValues(override),
        emptyText: 'No contacts selected.'
      };
    case ApprovalRuleOverrideType.ContactTags:
      return {
        values: override.ContactTags ?? [],
        emptyText: 'No contact tags selected.'
      };
    case ApprovalRuleOverrideType.None:
    default:
      return {
        values: [],
        emptyText: 'No override items configured.'
      };
  }
}

export function formatCityValues(override: RuleOverrideScope): string[] {
  const cityLabelsById = createSelectOptionLabelMap(override.InitialCities);

  return (override.CityIds ?? []).map((cityId) => cityLabelsById.get(cityId) ?? `City #${cityId}`);
}

export function formatContactValues(override: RuleOverrideScope): string[] {
  const contactLabelsById = createSelectOptionLabelMap(override.InitialContacts);

  return (override.ContactIds ?? []).map((contactId) => contactLabelsById.get(contactId) ?? `Contact #${contactId}`);
}

export function createSelectOptionLabelMap(options: SelectOption[] | undefined): Map<number, string> {
  const labelsById = new Map<number, string>();

  options?.forEach((option) => {
    const label = option.Label ?? String(option.Value);

    if (typeof option.NumericValue === 'number') {
      labelsById.set(option.NumericValue, label);
    }

    if (typeof option.Value === 'number') {
      labelsById.set(option.Value, label);
    }
  });

  return labelsById;
}
